import { AccessToken, AuthState } from "@okta/okta-auth-js";
import {
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { addBreadcrumb } from "@sentry/react";
import { oktaAuth } from "config/okta";

interface User {
  email?: string;
  name?: string;
  sub: string;
  groups?: string[];
}

interface AuthContextProps {
  user: User | null;
  isAuthenticated: boolean;
  isUnauthorized: boolean;
  signIn?: () => Promise<void>;
  signOut?: () => void;
  accessToken?: string;
  refreshToken?: string;
  error?: string;
  loading: boolean;
  getValidAccessToken?: () => Promise<string | undefined>;
  setUnauthorizedState?: () => void;
}

export const AuthContext = createContext<AuthContextProps | null>(null);

AuthContext.displayName = "AuthContext";

export const AuthProvider = (props: any): ReactElement => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isUnauthorized, setIsUnauthorized] = useState<boolean>(false);
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);
  const [refreshToken, setRefreshToken] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  
  // Debug mode: Force unauthorized state for testing
  const forceUnauthorized = window.location.search.includes("debug=unauthorized");

  // Get authorized group from environment
  const authorizedGroup = process.env.REACT_APP_OKTA_AUTHORIZED_GROUP;

  // Function to check if user is authorized (belongs to required group)
  const isUserAuthorized = useCallback((userGroups: string[] = []): boolean => {
    if (!authorizedGroup) {
      console.warn("REACT_APP_OKTA_AUTHORIZED_GROUP not configured, allowing all authenticated users");
      return true;
    }
    
    const hasAccess = userGroups.includes(authorizedGroup);
    return hasAccess;
  }, [authorizedGroup]);

  useEffect(() => {
    // If we're on callback route, let the callback component handle authentication
    const isCallbackRoute = window.location.pathname === "/login/callback";
    if (isCallbackRoute) {
      setLoading(false);
      return;
    }

    // Subscribe to auth state changes first
    const unsubscribe = oktaAuth.authStateManager.subscribe((authState: AuthState) => {
      
      // Debug mode: Force unauthorized state
      if (forceUnauthorized) {
        setIsAuthenticated(false);
        setIsUnauthorized(true);
        setUser(null);
        setLoading(false);
        return;
      }
      
      setAccessToken(authState.accessToken?.accessToken);
      setRefreshToken(authState.refreshToken?.refreshToken);
      
      if (authState.isAuthenticated && authState.idToken) {
        // With Org AS, get groups from User Info endpoint
        setLoading(true);
        
        oktaAuth.getUser().then((userInfo) => {
          const userGroups = Array.isArray(userInfo.groups) 
            ? userInfo.groups as string[]
            : [];
          
          // Check if user is authorized (belongs to required group)
          if (isUserAuthorized(userGroups)) {
            console.log("User authenticated and authorized");
            setIsAuthenticated(true);
            setIsUnauthorized(false);
            setUser({
              email: authState.idToken?.claims.email,
              name: authState.idToken?.claims.name,
              sub: authState.idToken?.claims.sub ?? "",
              groups: userGroups
            });
          } else {
            console.log("User authenticated but access denied - insufficient permissions");
            setIsAuthenticated(false);
            setIsUnauthorized(true);
            setUser(null);
          }
          
          // Set loading to false after everything is complete
          setLoading(false);
        }).catch((error) => {
          console.error("Error getting user info:", error);
          setIsAuthenticated(true);
          setIsUnauthorized(false);
          setUser({
            email: authState.idToken?.claims.email,
            name: authState.idToken?.claims.name,
            sub: authState.idToken?.claims.sub ?? "",
            groups: []
          });
          
          // Set loading to false even on error
          setLoading(false);
        });
      } else {
        setIsAuthenticated(false);
        setIsUnauthorized(false);
        setUser(null);
        setLoading(false);
      }
    });

    // Start the auth state manager
    void oktaAuth.start();

    return unsubscribe;
  }, [isUserAuthorized, forceUnauthorized]);


  const signIn = useCallback(async () => {
    try {
      setError(undefined);
      setLoading(true);
      
      addBreadcrumb({
        level: "info",
        category: "user_action",
        message: "User Sign In with Okta",
      });
      
      // Check if we're in a valid state to redirect
      if (!oktaAuth.options.clientId || !oktaAuth.options.issuer) {
        throw new Error("Okta configuration is missing clientId or issuer");
      }
      
      await oktaAuth.signInWithRedirect();
    } catch (err: any) {
      console.error("Sign in failed:", err);
      
      // Check for CORS/network errors common in Safari
      if (err.message?.includes("CORS") || err.message?.includes("preflight") || err.name === "NetworkError") {
        setError("Network or CORS error. Please check Okta configuration for localhost:3000 in Trusted Origins.");
      } else {
        setError("signIn-error");
      }
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      
      // 1. Clear local application state first
      setUser(null);
      setIsAuthenticated(false);
      setIsUnauthorized(false);
      setAccessToken(undefined);
      setRefreshToken(undefined);
      
      // 2. Clear all tokens from token manager
      await oktaAuth.tokenManager.clear();
      
      // 3. Clear all storage manually
      localStorage.clear();
      sessionStorage.clear();
      
      // 4. Use Okta SDK logout without redirect URI (since it's not configured)
      void oktaAuth.signOut();
      
      // 5. Force page reload to ensure clean state
      window.location.reload();
      
    } catch (err) {
      
      // Fallback: Clear everything and reload
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
    }
  }, []);

  const getValidAccessToken = useCallback(async (): Promise<string | undefined> => {
    try {
      const accessToken = await oktaAuth.tokenManager.get("accessToken") as AccessToken;
      
      if (accessToken && !oktaAuth.tokenManager.hasExpired(accessToken)) {
        return accessToken.accessToken;
      }
      
      const renewedToken = await oktaAuth.tokenManager.renew("accessToken") as AccessToken;
      return renewedToken?.accessToken;
    } catch (err) {
      console.error("Token refresh failed:", err);
      void signOut();
      return undefined;
    }
  }, [signOut]);

  // Add method to set unauthorized state (to be called from loginCallBack)
  const setUnauthorizedState = useCallback(() => {
    setIsUnauthorized(true);
    setIsAuthenticated(false);
    setUser(null);
    setAccessToken(undefined);
    setRefreshToken(undefined);
    setLoading(false);
  }, []);

  const authContextValue: AuthContextProps = useMemo(
    () => ({
      user,
      isAuthenticated,
      isUnauthorized,
      signIn,
      signOut,
      accessToken,
      refreshToken,
      error,
      loading,
      getValidAccessToken,
      setUnauthorizedState,
    }),
    [
      user,
      isAuthenticated,
      isUnauthorized,
      signIn,
      signOut,
      accessToken,
      refreshToken,
      error,
      loading,
      getValidAccessToken,
      setUnauthorizedState,
    ]
  );

  return (
    <AuthContext.Provider value={authContextValue} {...props} />
  ) as ReactElement;
};

export function useAuth(): AuthContextProps {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within a AuthProvider");
  }

  if (context === null) {
    throw new Error("AuthProvider supplied null context");
  }

  return context;
}
